import { FormattedMessage } from 'react-intl'
import { useSelector } from 'react-redux'
import { useLocation } from 'react-router'

import { SideBarBlockItem } from '@components/UI/SideBar'
import { ConnectedTrackedRoute } from '@components/UI/TrackedRoute'
import { INBOX_FILTER } from '@constants'
import { useMobileMenuControls } from '@context/MobileMenuControls'
import { EventTrackingNames } from '@enums'
import { PageRoutes } from '@enums'
import { useCampaignIdFromRoute } from '@hooks/useCampaignIdFromRoute'
import { useInboxFilter } from '@hooks/useInboxFilter'
import { useLocationSearch } from '@hooks/useLocationSearch'
import { IApplicationState } from '@store'
import { selectCurrentView } from '@store/router'
import { selectDefaultSortByFilter, selectSubmissionsStats } from '@store/submissions'
import { isEmpty, objectToQueryString } from '@utils'
import { isActiveMenu } from '@utils/urls'

interface IProps {
  filter: string
}

const InboxFiltersItem = ({ filter }: IProps) => {
  const campaignId = useCampaignIdFromRoute() as number
  const { hideMobileMenu } = useMobileMenuControls()
  const submissionsStats = useSelector((state: IApplicationState) =>
    selectSubmissionsStats(state, campaignId),
  )
  const defaultSortByFilters = useSelector((state: IApplicationState) =>
    selectDefaultSortByFilter(state),
  )
  const currentView = useSelector((state: IApplicationState) => selectCurrentView(state))

  const currentSearch = useLocationSearch()
  const { currentFilter } = useInboxFilter()
  const { pathname } = useLocation()
  const newSearch = objectToQueryString({
    filter,
    sort_by: defaultSortByFilters[filter],
    status: currentSearch && currentSearch.status,
  })
  const targetPathname = `/${PageRoutes.Campaigns}/${campaignId}`
  const count = submissionsStats[filter]

  const isOnInboxPage = pathname === targetPathname || pathname === `${targetPathname}/preview`
  const active =
    isOnInboxPage &&
    currentView === INBOX_FILTER &&
    isActiveMenu(pathname, PageRoutes.Campaigns, currentFilter, filter)

  const onClick = () => {
    hideMobileMenu()
  }

  if (isEmpty(filter)) {
    return null
  }

  return (
    <ConnectedTrackedRoute
      campaignId={campaignId}
      eventName={EventTrackingNames.SwitchInboxFilter}
      additionalProperties={{ count, target_filter: filter }}
    >
      <SideBarBlockItem
        key={filter}
        text={<FormattedMessage id={`inbox.sidebar.filters.${filter}`} />}
        count={count}
        to={{ pathname: targetPathname, search: newSearch }}
        active={active}
        showNotification={false}
        onClick={onClick}
        elementName={`side-bar-${filter}`}
      />
    </ConnectedTrackedRoute>
  )
}

export { InboxFiltersItem }
export default InboxFiltersItem
